import { addressSerializeMap } from "lib/serializers/address";
import { businessRelationSerializeMap } from "lib/serializers/businessRelations";
import { integrationBusinessSerializeMap } from "lib/serializers/integrationBusinesses";
import { vendorCommissionBandSerializeMap } from "lib/serializers/vendorCommissionBands";
import { vendorSplitSerializerMap } from "lib/serializers/vendorSplits";
import { buildDeserializer, buildSerializer } from "lib/serializerUtils";

import { bankingDetailsSerializeMap } from "./bankingDetails";
import { baseInterestSettingsSerializeMap } from "./interest";
import { tradingTermSerializeMap } from "./tradingTerms";

const businessUserSerializeMap = {
  email: "email",
  firstName: "first_name",
  lastName: "last_name",
  phoneNumber: "phone_number",
  isAccountsRecipient: "is_accounts_recipient",
  isReportRecipient: "is_report_recipient",
  isComplianceUser: "is_compliance_user",
  isCommercialUser: "is_commercial_user",
  saleYards: "sale_yards",
};

const businessEmailRecipientSerializeMap = {
  id: "id",
  email: "email",
  firstName: "first_name",
  lastName: "last_name",
  phoneNumber: "phone_number",
  isAccountsRecipient: "is_accounts_recipient",
  isComplianceReportRecipient: "is_compliance_report_recipient",
  isCommercialReportRecipient: "is_commercial_report_recipient",
  isEnvdRecipient: "is_envd_recipient",
};

const alternativesSerializeMap = {
  address: addressSerializeMap,
  hasDebtorInsurance: "has_debtor_insurance",
  hasTransitInsuranceForSales: "has_transit_insurance_for_sales",
  hasTransitInsuranceForPurchases: "has_transit_insurance_for_purchases",
  isActive: "is_active",
  isTopBuyerSheep: "is_top_buyer_sheep",
  isTopBuyerCattle: "is_top_buyer_cattle",
  publicDisplayName: "public_display_name",
  shortCode: "short_code",
  shortCodeSaleyard: "short_code_saleyard",
  shortCodeAuctionsPlus: "short_code_auctions_plus",
  slug: "slug",
  source: "source",
  lastModified: "last_modified",
};

const sourceSerializeMap = {
  id: "id",
  type: "type",
};

const alternativesBusinessPropertySerializeMap = {
  address: addressSerializeMap,
  id: "id",
  isShown: "is_shown",
  name: "name",
  source: sourceSerializeMap,
};

const businessPropertySerializeMap = {
  alternatives: alternativesBusinessPropertySerializeMap,
  id: "id",
  isShown: "is_shown",
  name: "name",
  source: sourceSerializeMap,
  address: addressSerializeMap,
  // These are WRITE ONLY fields...  ideally we would REMOVE them on read, as they will always be blank.
  accreditationEu: "accreditation_eu",
  accreditationJbas: "accreditation_jbas",
  accreditationNee: "accreditation_nee", // TODO Remove as part of AG-5172
  accreditationPcas: "accreditation_pcas",
  district: "district",
  lpaNumber: "lpa_number",
  msaNumber: "msa_number",
  defaultForSaleyardIds: "default_for_saleyard_ids",
};

const buyerWaySerializeMap = {
  destinationPropertyId: "destination_property_id",
  destinationPropertyPic: "destination_property_PIC",
  id: "id",
  isShown: "is_shown",
  name: "name",
  saleyardIds: "saleyard_ids",
};

const businessV2SerializeMap = {
  abn: "abn",
  abrLastChecked: "abr_last_checked",
  balanceCents: "balance_cents",
  acn: "acn",
  addedInLivestockSaleId: "added_in_livestock_sale_id",
  address: addressSerializeMap,
  alternatives: alternativesSerializeMap,
  bankingDetails: bankingDetailsSerializeMap,
  bpayCustomerReferenceNumber: "bpay_customer_reference_number",
  branchId: "branch_id",
  brands: "brands",
  businessDeploymentId: "business_deployment_id",
  businessLivestockAgentId: "business_livestock_agent_id",
  businessUsers: businessUserSerializeMap,
  buyerWays: buyerWaySerializeMap,
  created: "created",
  myobCustomerCardId: "myob_customer_card_id",
  defaultConsigningDeploymentId: "default_consigning_deployment_id",
  defaultDestinations: {
    propertyId: "property_id",
    saleyardId: "saleyard_id",
  },
  emailRecipients: businessEmailRecipientSerializeMap,
  generateBpayCrn: "generate_bpay_crn",
  hasDebtorInsurance: "has_debtor_insurance",
  hasTransitInsuranceForSales: "has_transit_insurance_for_sales",
  hasTransitInsuranceForPurchases: "has_transit_insurance_for_purchases",
  id: "id",
  integrationBusinesses: integrationBusinessSerializeMap,
  interestSettings: baseInterestSettingsSerializeMap,
  tradingTerms: tradingTermSerializeMap,
  overrideInterestSettings: "override_interest_settings",
  isActive: "is_active",
  isBuyer: "is_buyer",
  isABNActive: "is_abn_active",
  isGSTRegistered: "is_gst_registered",
  isHobbyFarmer: "is_hobby_farmer",
  isMeatProcessorCommercialBuyer: "is_meat_processor_commercial_buyer",
  isPublished: "is_published",
  isTemporary: "is_temporary",
  isTopBuyerCattle: "is_top_buyer_cattle",
  isTopBuyerSheep: "is_top_buyer_sheep",
  isTransporter: "is_transporter",
  isVendor: "is_vendor",
  lastModified: "last_modified",
  name: "name",
  notes: "notes",
  otherEmailRecipients: businessEmailRecipientSerializeMap,
  pk: "pk",
  primaryContactEmail: "primary_contact_email",
  properties: businessPropertySerializeMap,
  publicDisplayName: "public_display_name",
  sideLoadedDataIds: "side_loaded_data_ids",
  shortCode: "short_code",
  shortCodeAuctionsPlus: "short_code_auctions_plus",
  shortCodeSaleyard: "short_code_saleyard",
  shouldChargeGSTOverride: "should_charge_gst_override",
  slug: "slug",
  myobSupplierCardId: "myob_supplier_card_id",
  tradingName: "trading_name",
  vendorCommissionBands: vendorCommissionBandSerializeMap,
  defaultVendorSplit: vendorSplitSerializerMap,
  defaultBuyerSplit: vendorSplitSerializerMap,
  isGlobal: "is_global",
  labelIds: "label_ids",
  xeroBankingLocked: "xero_banking_locked",
};

const nestedBusinessV2SerializeMap = {
  address: "address",
  banking_details: "bankingDetails",
  business_users: "businessUsers",
  buyer_ways: "buyerWays",
  default_destinations: "defaultDestinations",
  default_vendor_split: "defaultVendorSplit",
  default_buyer_split: "defaultBuyerSplit",
  email_recipients: "emailRecipients",
  integration_businesses: "integrationBusinesses",
  interest_settings: "interestSettings",
  trading_terms: "tradingTerms",
  other_email_recipients: "otherEmailRecipients",
  vendor_commission_bands: "vendorCommissionBands",
};

const businessV2SerializeWriteMap = {
  ...businessV2SerializeMap,
  relationships: businessRelationSerializeMap,
};

const nestedBusinessV2SerializeWriteMap = {
  ...nestedBusinessV2SerializeMap,
  relationships: "relationships",
};

export const serializeBusiness = buildSerializer(
  businessV2SerializeWriteMap,
  "businessV2",
  nestedBusinessV2SerializeWriteMap,
);

export const deserializeBusiness = buildDeserializer(
  businessV2SerializeMap,
  "businessV2",
  nestedBusinessV2SerializeMap,
  {
    name: null,
    properties: [],
    buyerWays: [],
    isActive: true,
    emailRecipients: [],
    otherEmailRecipients: [],
    address: null,
    labelIds: [],
  },
);

const globalBusinessesSerializerMap = {
  id: "id",
  name: "name",
};

export const serializeGlobalBusinesses = buildSerializer(
  globalBusinessesSerializerMap,
  "globalBusinesses",
);

export const deserializeGlobalBusinesses = buildDeserializer(
  globalBusinessesSerializerMap,
  "globalBusinesses",
);
